/**
 * Survey Photo Gallery Component
 *
 * Displays all photos uploaded for a specific survey in a gallery view
 * with lightbox support for viewing individual images.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon, X, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import Button from '../../components/Button';
import Layout from '../../components/Layout';

interface SurveyImage {
  s3_key: string;
  session_id: number;
  question_id: string;
  filename: string;
  url: string;
}

interface ImagesResponse {
  images: SurveyImage[];
  count: number;
  survey_id: number;
}

const SurveyPhotoGallery: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [images, setImages] = useState<SurveyImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Support for group surveys with multiple survey IDs
  const surveyIds = searchParams.get('surveyIds');
  const groupId = searchParams.get('groupId');

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    loadImages();
  }, [id, surveyIds]);

  const loadImages = async () => {
    try {
      setIsLoading(true);
      setError(null);

      let url: string;
      if (surveyIds) {
        // Multiple surveys (group survey)
        url = `${import.meta.env.VITE_API_URL}/api/photo-upload/list-images-by-surveys?survey_ids=${surveyIds}`;
      } else if (id) {
        // Single survey
        url = `${import.meta.env.VITE_API_URL}/api/photo-upload/list-images-by-survey/${id}`;
      } else {
        setError('No survey ID provided');
        setIsLoading(false);
        return;
      }

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load images');
      }

      const data: ImagesResponse = await response.json();
      setImages(data.images || []);
    } catch (err) {
      console.error('Failed to load images:', err);
      setError('Failed to load images. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (groupId) {
      navigate(`/survey-groups/${groupId}`);
    } else if (id) {
      navigate(`/surveys/${id}`);
    } else {
      navigate('/surveys');
    }
  };

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  // Lock body scroll while the lightbox is open, and ALWAYS restore it on close or unmount — so
  // navigating away (browser back, route change) with the lightbox open can't leave the whole app
  // scroll-locked via a stranded `overflow: hidden` on <body>.
  useEffect(() => {
    if (!lightboxOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [lightboxOpen]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;

      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, goToPrevious, goToNext]);

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading images...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={handleBack}
              icon={<ArrowLeft className="w-4 h-4 mr-1" />}
            >
              {groupId ? 'Back to Survey Group' : 'Back to Survey'}
            </Button>

            <div className="text-sm text-gray-600">
              {images.length} image{images.length !== 1 ? 's' : ''} found
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Main Content */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center mb-6">
              <ImageIcon className="w-6 h-6 text-blue-500 mr-2" />
              <h1 className="text-2xl font-bold text-gray-900">Survey Photo Gallery</h1>
            </div>

            {images.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {images.map((image, index) => (
                  <div
                    key={image.s3_key}
                    className="relative aspect-square overflow-hidden rounded-lg bg-gray-100 cursor-pointer group"
                    onClick={() => openLightbox(index)}
                  >
                    <img
                      src={image.url}
                      alt={image.filename}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                      <p className="text-xs text-white truncate">Session {image.session_id}</p>
                      <p className="text-xs text-white/80 truncate">{image.filename}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Images Found</h3>
                <p className="text-gray-600">No photos have been uploaded for this survey yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && images.length > 0 && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/95 overflow-hidden">
          {/* Lightbox Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-black/80">
            <div className="flex-1 min-w-0">
              <p className="font-mono text-sm text-teal-400 truncate">
                {images[currentIndex].s3_key}
              </p>
            </div>
            <div className="flex items-center gap-4 ml-4">
              <span className="text-gray-400 text-sm whitespace-nowrap">
                {currentIndex + 1} / {images.length}
              </span>
              <button
                onClick={closeLightbox}
                className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Lightbox Content */}
          <div className="flex-1 min-h-0 flex items-center justify-center relative px-16 py-4">
            {/* Previous Button */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors z-10"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>

            {/* Image */}
            <img
              src={images[currentIndex].url}
              alt={images[currentIndex].filename}
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: 'calc(100vh - 140px)' }}
            />

            {/* Next Button */}
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors z-10"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </button>
          </div>

          {/* Lightbox Footer */}
          <div className="flex-shrink-0 px-4 py-3 bg-black/80 text-center">
            <p className="text-sm text-gray-400">
              Session: {images[currentIndex].session_id} | Question:{' '}
              {images[currentIndex].question_id}
            </p>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default SurveyPhotoGallery;
